import React, { useMemo, useState, useEffect } from "react";
import { useTheme, alpha } from "@mui/material/styles";

import {
  Box,
  CircularProgress,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  Button,
  MenuItem,
  Select,
  FormControl,
  Fade,
  Slide,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SortOutlinedIcon from "@mui/icons-material/SortOutlined";

import { ref, onValue } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";

const DEFAULT_BANNER = "https://images.unsplash.com/photo-1492684223066-81342ee5ff30";

const CATEGORIES = [
  "Todos",
  "Música",
  "Festival",
  "Show",
  "Festa",
  "Tech",
  "Negócios",
  "Comedy",
  "Esporte",
  "Arte & Cultura",
  "Gastronomia",
  "Outros",
];

function safeLower(v) {
  return (v || "").toString().toLowerCase().trim();
}

export default function Events() {
  const theme = useTheme();

  const [dbEvents, setDbEvents] = useState([]);
  const [loadingDb, setLoadingDb] = useState(true);

  useEffect(() => {
    const indexRef = ref(rtdb, "publicEventIndex");
    const unsub = onValue(
      indexRef,
      (snap) => {
        const val = snap.val();
        const list = val
          ? Object.entries(val).map(([id, ev]) => ({
              id,
              slug: ev.slug || id,
              title: ev.title || "Evento",
              category: ev.category || "Evento",
              dateLabel: ev.startAtMs
                ? new Date(Number(ev.startAtMs)).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "Data a definir",
              dateISO: ev.startAtMs
                ? new Date(Number(ev.startAtMs)).toISOString().slice(0, 10)
                : "",
              location:
                ev.city && ev.state
                  ? `${ev.city} · ${ev.state}`
                  : ev.city || "",
              city: ev.city || "",
              uf: ev.state || "",
              priceFrom: Number(ev.minTicketPrice || 0),
              priceLabel: Number(ev.minTicketPrice || 0).toLocaleString(
                "pt-BR",
                { style: "currency", currency: "BRL" },
              ),
              image: ev.bannerUrl || DEFAULT_BANNER,
              featured: false,
            }))
          : [];
        setDbEvents(list);
        setLoadingDb(false);
      },
      () => setLoadingDb(false),
    );
    return () => unsub();
  }, []);

  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("Todos");
  const [sort, setSort] = useState("relevance");

  const [page, setPage] = useState(1);
  const pageSize = 9;

  const filtered = useMemo(() => {
    const q = safeLower(query);
    const c = safeLower(city);

    let list = [...dbEvents];

    list = list.filter((ev) => {
      const matchQuery =
        !q ||
        safeLower(ev.title).includes(q) ||
        safeLower(ev.category).includes(q) ||
        safeLower(ev.location).includes(q);

      const matchCity =
        !c ||
        safeLower(ev.city).includes(c) ||
        safeLower(ev.location).includes(c);

      const matchCategory =
        category === "Todos" ? true : ev.category === category;

      return matchQuery && matchCity && matchCategory;
    });

    if (sort === "date_asc")
      list.sort((a, b) => (a.dateISO || "").localeCompare(b.dateISO || ""));
    if (sort === "price_asc")
      list.sort((a, b) => (a.priceFrom ?? 0) - (b.priceFrom ?? 0));
    if (sort === "featured")
      list.sort((a, b) => Number(b.featured) - Number(a.featured));

    return list;
  }, [dbEvents, query, city, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [query, city, category, sort]);

  const clearFilters = () => {
    setQuery("");
    setCity("");
    setCategory("Todos");
    setSort("relevance");
  };

  // --- tokens do theme ---
  const paper = theme.palette.background.paper; // #08162b
  const divider = theme.palette.divider; // rgba(...)
  const primary = theme.palette.primary.main; // #1aff7a

  const glass = {
    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
    background: alpha(theme.palette.common.white, 0.04),
    boxShadow: "0 16px 60px rgba(0,0,0,0.35)",
    backdropFilter: "blur(16px)",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.2,
      background: alpha(theme.palette.common.black, 0.18),
      "& fieldset": { borderColor: divider },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.common.white, 0.22),
      },
      "&.Mui-focused fieldset": { borderColor: alpha(primary, 0.7) },
    },
    "& .MuiInputBase-input::placeholder": { opacity: 0.7 },
  };

  return (
    <Box sx={{ pb: { xs: 6, md: 8 } }}>
      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 5 },
          overflow: "hidden",
        }}
      >
        {/* fundo premium (puxando cor do primary do theme) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(900px 420px at 10% 20%, ${alpha(primary, 0.18)}, transparent 60%),
              radial-gradient(800px 420px at 90% 10%, ${alpha(primary, 0.1)}, transparent 55%),
              linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.04)}, transparent)
            `,
            pointerEvents: "none",
          }}
        />

        <Container
          maxWidth={false}
          sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}
        >
          <Slide direction="up" in timeout={550}>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  letterSpacing: "0.14em",
                  fontSize: 12,
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                VITRINE
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 950,
                  letterSpacing: "-0.03em",
                  fontSize: { xs: 36, sm: 44, md: 52 },
                  lineHeight: 1.05,
                  mb: 1.2,
                }}
              >
                Explore eventos incríveis
              </Typography>

              <Typography
                sx={{ maxWidth: "70ch", opacity: 0.78, lineHeight: 1.7 }}
              >
                Busque por cidade, categoria e encontre o rolê perfeito.
                Checkout rápido, seguro e com transparência.
              </Typography>
            </Box>
          </Slide>

          {/* SEARCH / FILTER BAR */}
          <Fade in timeout={750}>
            <Box
              sx={{
                mt: 3,
                p: { xs: 1.3, sm: 1.6 },
                borderRadius: 3,
                border: `1px solid ${divider}`,
                ...glass,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Pesquisar por nome do evento, artista, local..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ opacity: 0.85 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <TextField
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Cidade (opcional)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PlaceOutlinedIcon sx={{ opacity: 0.85 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: { xs: "100%", md: 260 },
                    ...inputSx,
                  }}
                />

                <FormControl sx={{ minWidth: { xs: "100%", md: 220 } }}>
                  <Select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <SortOutlinedIcon sx={{ opacity: 0.85 }} />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2.2,
                      background: alpha(theme.palette.common.black, 0.18),
                      "& .MuiSelect-select": { pl: 1.5 },
                      "& fieldset": { borderColor: divider },
                      "&:hover fieldset": {
                        borderColor: alpha(theme.palette.common.white, 0.22),
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: alpha(primary, 0.7),
                      },
                    }}
                  >
                    <MenuItem value="relevance">Relevância</MenuItem>
                    <MenuItem value="featured">Destaques primeiro</MenuItem>
                    <MenuItem value="date_asc">Mais próximos</MenuItem>
                    <MenuItem value="price_asc">Menor preço</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  sx={{
                    borderRadius: 999, // respeita o theme
                    fontWeight: 900,
                    px: 2.2,
                    whiteSpace: "nowrap",
                    borderColor: alpha(theme.palette.common.white, 0.22),
                    "&:hover": {
                      borderColor: alpha(theme.palette.common.white, 0.32),
                      background: alpha(theme.palette.common.white, 0.04),
                    },
                  }}
                >
                  Limpar
                </Button>
              </Stack>

              <Divider sx={{ my: 1.4, opacity: 0.25, borderColor: divider }} />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {CATEGORIES.map((c) => {
                  const active = category === c;
                  return (
                    <Chip
                      key={c}
                      label={c}
                      onClick={() => setCategory(c)}
                      icon={c === "Todos" ? <EventOutlinedIcon /> : undefined}
                      sx={{
                        fontWeight: 900,
                        borderRadius: 999,
                        border: `1px solid ${
                          active
                            ? alpha(primary, 0.38)
                            : alpha(theme.palette.common.white, 0.16)
                        }`,
                        background: active
                          ? alpha(primary, 0.16)
                          : alpha(theme.palette.common.white, 0.04),
                        opacity: active ? 1 : 0.9,
                        "& .MuiChip-icon": { color: "inherit", opacity: 0.9 },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* RESULTS */}
      <Container
        maxWidth={false}
        sx={{ maxWidth: "1120px", px: { xs: 2, sm: 2.5 } }}
      >
        {loadingDb ? (
          <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loadingDb && (
        <>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 2.2 }}
        >
          <Typography sx={{ opacity: 0.75 }}>
            {filtered.length} resultado{filtered.length === 1 ? "" : "s"}
            {category !== "Todos" ? ` · ${category}` : ""}
            {city ? ` · ${city}` : ""}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ fontSize: 12, opacity: 0.65 }}>Página</Typography>
            <Chip
              label={`${page} / ${totalPages}`}
              sx={{
                borderRadius: 999,
                background: alpha(theme.palette.common.white, 0.06),
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                fontWeight: 900,
              }}
            />
          </Stack>
        </Stack>

        {filtered.length === 0 ? (
          <Box
            sx={{
              mt: 2,
              p: { xs: 2.2, md: 3 },
              borderRadius: 3,
              border: `1px solid ${divider}`,
              background: alpha(theme.palette.common.white, 0.04),
              boxShadow: "0 12px 50px rgba(0,0,0,0.28)",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontWeight: 950, fontSize: 20, mb: 0.6 }}>
              Nada por aqui 😅
            </Typography>
            <Typography sx={{ opacity: 0.75, mb: 2 }}>
              Tenta remover alguns filtros ou pesquisar por outra
              cidade/categoria.
            </Typography>
            <Button
              onClick={clearFilters}
              variant="outlined"
              sx={{ borderRadius: 999, fontWeight: 900, px: 3 }}
            >
              Limpar filtros
            </Button>
          </Box>
        ) : (
          <>
            {/* GRID */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {pageItems.map((ev, idx) => (
                <Slide key={ev.id} direction="up" in timeout={450 + idx * 90}>
                  <Fade in timeout={650 + idx * 90}>
                    <Box
                      component={RouterLink}
                      to={`/eventos/${ev.id}`}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: `1px solid ${divider}`,
                        background: alpha(theme.palette.common.white, 0.04),
                        boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                        transition: "all .35s ease",
                        textDecoration: "none",
                        color: "inherit",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: "0 18px 60px rgba(0,0,0,0.38)",
                          borderColor: alpha(theme.palette.common.white, 0.18),
                        },
                      }}
                    >
                      {/* IMAGE */}
                      <Box
                        sx={{
                          height: 190,
                          backgroundImage: `url(${ev.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          position: "relative",
                        }}
                      >
                        {/* badge destaque */}
                        {ev.featured && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 12,
                              px: 1.2,
                              py: 0.6,
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 950,
                              letterSpacing: "0.08em",
                              background: alpha("#FFD700", 0.14),
                              border: `1px solid ${alpha("#FFD700", 0.25)}`,
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            DESTAQUE
                          </Box>
                        )}
                      </Box>

                      {/* BODY */}
                      <Box
                        sx={{
                          p: 2,
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                        }}
                      >
                        <Box
                          sx={{
                            alignSelf: "flex-start",
                            px: 1.2,
                            py: 0.5,
                            mb: 1,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 900,
                            background: alpha(primary, 0.16),
                            border: `1px solid ${alpha(primary, 0.24)}`,
                          }}
                        >
                          {ev.category}
                        </Box>

                        <Typography
                          sx={{ fontWeight: 950, fontSize: 18, mb: 0.5 }}
                        >
                          {ev.title}
                        </Typography>

                        <Typography
                          sx={{ fontSize: 13, opacity: 0.72, mb: 1.6 }}
                        >
                          {ev.dateLabel} · {ev.location}
                        </Typography>

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: "auto" }}
                        >
                          <Typography sx={{ fontWeight: 950, fontSize: 16 }}>
                            {ev.priceLabel}
                            <Typography
                              component="span"
                              sx={{ fontSize: 12, opacity: 0.6, ml: 0.5 }}
                            >
                              a partir
                            </Typography>
                          </Typography>

                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              borderRadius: 999,
                              fontWeight: 900,
                              px: 2,
                              borderColor: alpha(
                                theme.palette.common.white,
                                0.22,
                              ),
                              "&:hover": {
                                borderColor: alpha(primary, 0.6),
                                background: alpha(primary, 0.08),
                              },
                            }}
                          >
                            Ver ingressos
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Fade>
                </Slide>
              ))}
            </Box>

            {/* PAGINATION */}
            <Stack
              direction="row"
              spacing={1.2}
              justifyContent="center"
              sx={{ mt: 3.2 }}
            >
              <Button
                variant="outlined"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                sx={{ borderRadius: 999, fontWeight: 900, px: 2.4 }}
              >
                Anterior
              </Button>
              <Button
                variant="outlined"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                sx={{ borderRadius: 999, fontWeight: 900, px: 2.4 }}
              >
                Próxima
              </Button>
            </Stack>
          </>
        )}
        </>
        )}
      </Container>
    </Box>
  );
}
